<template>

  <div id="ts-bg">
    <svg :viewBox="viewBoxString">
      <g :transform="rect.translate">
        <rect class="ts" :transform="rotate" :width="rect.width" :height="rect.height" />
      </g>
    </svg>
  </div>

</template>

<script>

import Vector2D from '@/libs/vector2D';
import RandomNumber from '@/libs/randomNumber';

export default {

  name: 'TsTinySquare',

  data() {
    return {

      count: 0,

      rect: {
        width: 6,
        height: 6,
        x: 0,
        y: 0,
        translate: "translate("+window.innerWidth/2+" "+window.innerHeight/2+")",
        startRadius: 0,
        position: Vector2D.create(window.innerWidth/2, window.innerHeight/2),
        maxSpeed: 1,
        acceleration: Vector2D.create(),
        velocity: Vector2D.create(RandomNumber(5, -2), RandomNumber(5, -2)),
        maxForce: 1,
        wanderTheta: 0.005
      }
    }
  },

  computed: {
    viewBoxString () {
      return `0 0 ${window.innerWidth} ${window.innerHeight}`
    },
    rotate() {
      return "rotate("+ this.count +")";
    }
  },

  methods: {

    animate() {
      this.update();
      requestAnimationFrame(this.animate.bind(this));
    },

    update() {
      if (this.count == 356) this.count = 0;
      this.count += 0.5;
      this.rect.translate = "translate(" + this.rect.position[0] + " " + this.rect.position[1] + ")";
      this.applyPhysics();
      this.applyMovement();
    },

    applyPhysics() {
      this.rect.maxSpeed = 1;
      this.wander(RandomNumber(3, 5), RandomNumber(150, 300), RandomNumber(-0.05, 0.05));
      this.borders(window.innerWidth,window.innerHeight,0);
    },

    applyMovement() {
      Vector2D.add(this.rect.velocity, this.rect.velocity, this.rect.acceleration);
      Vector2D.limit(this.rect.velocity, this.rect.velocity, this.rect.maxSpeed);
      Vector2D.add(this.rect.position, this.rect.position, this.rect.velocity);
      Vector2D.multiply(this.rect.acceleration, this.rect.acceleration, [0, 0]);
    },

    /**
     * ARRIVE
     * @param {target}
     * @param {position}
     * @param {minD} minimum distance
     */

    arrive(t, p, minD) {
      if (!minD || minD < 100) {
        minD = 100
      };
      var steeringforce, distance, desiredVel;
      // calculate the distance
      distance = Vector2D.distance(t, p);
      // get the distance desired velocity
      desiredVel = Vector2D.subtract(t, t, p);
      // normalize the desired velocity
      Vector2D.normalize(desiredVel, desiredVel);
      // if we are within minimum distance slow down
      if (distance < minD) {
        desiredVel = Vector2D.multiply(desiredVel, desiredVel, [this.rect.maxSpeed * (distance / minD), this.rect.maxSpeed * (distance / minD)]);
        Vector2D.subtract(desiredVel, desiredVel, this.rect.velocity);
      } else {
        // proceed at maximum speed
        Vector2D.multiply(desiredVel, desiredVel, [this.rect.maxSpeed, this.rect.maxSpeed]);
      }
      // make sure we son't exceed maximum speed
      Vector2D.limit(desiredVel, desiredVel, this.rect.maxSpeed);
      return this.steer(desiredVel);
    },

    /**
     * SEEK
     * @param {target}
     * @param {position}
     */

    seek(t, p) {
      var target = Vector2D.subtract(t, t, p);
      return this.steer(target);
    },

    /**
     * WANDER
     * @param {r} radius
     * @param {d} distance
     * @param {c} change
     */

    wander(r, d, c) {
      var circlePos, wanderTarget;
      // Wander radius
      var wanderR = r;
      // Wander distance
      var wanderD = d;
      // Wander change
      var wanderC = c;
      // Calculate the new location to steer towards the wander circle
      circlePos = Vector2D.clone(this.rect.velocity);
      Vector2D.normalize(circlePos, circlePos);
      Vector2D.multiply(circlePos, circlePos, [wanderD, wanderD]);
      Vector2D.add(circlePos, circlePos, this.rect.position);
      // Wander random theta
      this.rect.wanderTheta += Math.random() * (Math.floor(Math.random() * 2) == wanderC ? wanderC : -wanderC) - (Math.floor(Math.random() * 2) == wanderC ? wanderC : -wanderC) * .5;
      // Offset the wander theta
      wanderTarget = [Math.cos(this.rect.wanderTheta) * wanderR, Math.sin(this.rect.wanderTheta) * wanderR];
      Vector2D.add(wanderTarget, wanderTarget, circlePos);
      // seek the target destination
      return this.seek(wanderTarget, this.rect.position);
    },

    /**
     * STEER — Implement Craig Reynolds' steering algorithm
     * @param {target}
     * Finalize the steering behaviour
     */

    steer(target) {
      var steer;
      var length = Vector2D.length(target, target);
      // normalize and scale vector
      Vector2D.normalize(target, target);
      Vector2D.multiply(target, target, [this.rect.maxSpeed, this.rect.maxSpeed]);
      // calculate the steer force
      steer = target;
      Vector2D.subtract(steer, steer, this.rect.velocity);
      Vector2D.limit(steer, steer, this.rect.maxForce);
      Vector2D.add(this.rect.acceleration, this.rect.acceleration, steer);
      return steer;
    },

    /**
     * BORDERS
     * @param {width}
     * @param {height}
     * @param {radius}
     */

    borders(width, height, radius) {
      if (this.rect.position[0] < -radius) this.rect.position[0] = width + radius;
      if (this.rect.position[1] < -radius) this.rect.position[1] = height + radius;
      if (this.rect.position[0] > width + radius) this.rect.position[0] = -0;
      if (this.rect.position[1] > height + radius) this.rect.position[1] = -0;
    },

    /**
     * HELPER(S)
     * ================
     */

    getNormalPoint(p, a, b) {
      var ap = Vector2D.clone(p);
      var ab = Vector2D.clone(b);
      // Perform scalar projection calculations
      Vector2D.subtract(ap, ap, a);
      Vector2D.subtract(ab, ab, a);
      Vector2D.normalize(ab, ab);
      Vector2D.scale(ab, ab, Vector2D.dot(ap, ab));
      var normalPoint = Vector2D.add(Vector2D.create(), Vector2D.clone(a), ab);
      return normalPoint;
    },

  },

  mounted() {
    this.animate();
  }

}

</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">

#ts-bg {
  position: fixed;
  top: 0px;
  left: 0px;
  z-index: -1;
  width: 100%;
  height: 100%;

  .ts {
    fill: #3D348B;
    transition: fill .5s ease;
  }
}

div#app.dark {
  .ts {
    fill: rgba(255,255,255,1);
  }
}

</style>
